import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Space, theme, Button } from 'antd';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  BookOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  BarsOutlined,
  SolutionOutlined,
  TrophyOutlined,
  CodeOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header, Sider, Content } = AntLayout;

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { token } = theme.useToken();
  
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';

  const studentMenuItems = [
    {
      key: '/student',
      icon: <DashboardOutlined />,
      label: '学习仪表盘',
    },
    {
      key: '/student/exercises',
      icon: <FileTextOutlined />,
      label: '我的练习',
    },
    {
      key: '/student/profile',
      icon: <UserOutlined />,
      label: '个人信息',
    }
  ];

  const teacherMenuItems = [
    {
      key: '/teacher',
      icon: <DashboardOutlined />,
      label: '教师仪表盘',
    },
    {
      key: '/teacher/exercises',
      icon: <BarsOutlined />,
      label: '练习管理',
    },
    {
      key: '/teacher/create-exercise',
      icon: <FileTextOutlined />,
      label: '创建练习',
    },
    {
      key: '/teacher/assign-exercise',
      icon: <SolutionOutlined />,
      label: '分配练习',
    },
    {
      key: '/teacher/students',
      icon: <TeamOutlined />,
      label: '学生管理',
    }
  ];

  const menuItems = isTeacher ? teacherMenuItems : studentMenuItems;
  
  // 直接从URL确定当前选中的菜单项
  const [selectedKey, setSelectedKey] = useState(() => {
    const path = location.pathname;
    
    // 精确匹配
    for (const item of menuItems) {
      if (path === item.key) {
        return item.key;
      }
    }
    
    // 前缀匹配
    for (const item of menuItems) {
      if (path.startsWith(`${item.key}/`)) {
        return item.key;
      }
    }
    
    // 默认选中首页
    return isTeacher ? '/teacher' : '/student';
  });
  
  // 当URL变化时更新选中项
  useEffect(() => {
    const path = location.pathname;
    
    // 精确匹配
    for (const item of menuItems) {
      if (path === item.key) {
        setSelectedKey(item.key);
        return;
      }
    }
    
    // 前缀匹配
    for (const item of menuItems) {
      if (path.startsWith(`${item.key}/`)) {
        setSelectedKey(item.key);
        return;
      }
    }
    
    // 默认选中首页
    setSelectedKey(isTeacher ? '/teacher' : '/student');
  }, [location.pathname, isTeacher, menuItems]);

  // 用户菜单已移至Dropdown的menu属性中

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
          zIndex: 10
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
          <h1 style={{ 
            color: token.colorPrimary, 
            fontSize: collapsed ? '16px' : '20px',
            transition: 'all 0.2s',
            margin: 0
          }}>
            {collapsed ? '编程' : '少儿编程平台'}
          </h1>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selectedKey]}
          defaultSelectedKeys={[isTeacher ? '/teacher' : '/student']}
          style={{ 
            borderRight: 0
          }}
          className="sidebar-menu"
          items={menuItems}
          onClick={e => {
            navigate(e.key);
            setSelectedKey(e.key);
            
            setTimeout(() => {
              const menuItems = document.querySelectorAll('.sidebar-menu .ant-menu-item');
              menuItems.forEach(item => {
                if (item.classList.contains('ant-menu-item-selected')) {
                  item.style.backgroundColor = '#e6f7ff';
                  item.style.color = '#1890ff';
                  item.style.fontWeight = 'bold';
                } else {
                  item.style.backgroundColor = 'transparent';
                  item.style.color = 'rgba(0, 0, 0, 0.85)';
                  item.style.fontWeight = 'normal';
                }
              });
            }, 0);
          }}
        />
      </Sider>
      <AntLayout>
        <Header style={{ 
          background: '#fff', 
          padding: '0 16px', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
        }}>
          {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
            className: 'trigger',
            style: { fontSize: '18px' },
            onClick: () => setCollapsed(!collapsed),
          })}
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* 编程环境下拉菜单 */}
            {!isTeacher && (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'cpp',
                      icon: <CodeOutlined />,
                      label: (
                        <div style={{ padding: '4px 0' }}>
                          <div style={{ fontWeight: 'bold' }}>C++编程环境</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-color-secondary)' }}>使用C++语言编写和运行代码</div>
                        </div>
                      ),
                      onClick: () => {
                        const token = localStorage.getItem('token');
                        if (token) {
                          window.open(`https://code.openaitool.cn/?token=${encodeURIComponent(token)}&language=cpp`, '_blank');
                        } else {
                          console.error('未找到有效的token');
                        }
                      }
                    },
                    {
                      key: 'python',
                      icon: <CodeOutlined />,
                      label: (
                        <div style={{ padding: '4px 0' }}>
                          <div style={{ fontWeight: 'bold' }}>Python编程环境</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-color-secondary)' }}>使用Python语言编写和运行代码</div>
                        </div>
                      ),
                      onClick: () => {
                        const token = localStorage.getItem('token');
                        if (token) {
                          window.open(`https://code.openaitool.cn/?token=${encodeURIComponent(token)}&language=python`, '_blank');
                        } else {
                          console.error('未找到有效的token');
                        }
                      }
                    }
                  ],
                  style: {
                    width: '240px',
                    padding: '8px 0'
                  }
                }}
                placement="bottomCenter"
                arrow={true}
                trigger={['click']}
                overlayStyle={{ boxShadow: 'var(--box-shadow)' }}
              >
                <Button 
                  type="primary" 
                  icon={<CodeOutlined />} 
                  style={{ 
                    marginRight: '16px',
                    background: 'var(--primary-color)',
                    borderRadius: 'var(--border-radius-md)',
                    boxShadow: '0 2px 0 rgba(0,0,0,0.045)',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.3s'
                  }}
                  className="code-env-button"
                >
                  进入编程环境
                </Button>
              </Dropdown>
            )}

            {/* 用户菜单 */}
            <Dropdown menu={{ items: [
              {
                key: '1',
                icon: <UserOutlined />,
                label: '个人信息',
                onClick: () => navigate(isTeacher ? '/teacher/profile' : '/student/profile')
              },
              {
                key: '2',
                icon: <LogoutOutlined />,
                label: '退出登录',
                onClick: logout
              }
            ] }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} src={user?.avatar} />
                <span>{user?.name || '用户'}</span>
              </Space>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ 
          margin: '24px 16px', 
          padding: 24, 
          minHeight: 280, 
          background: '#fff',
          borderRadius: '4px'
        }}>
          <Outlet />
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;  
